import { Component, JSXElement, Show, onCleanup, onMount } from 'solid-js';
import { BtnClasses } from '../../types/common';
import Icon from '../Icon/Icon';
import { IconColor } from '../Icon/icon-color';
import type { IconSize } from '../Icon/icon-size';
import { MODUS_BUTTON_INVERSE_STYLESHEET } from '../../mc-custom-css/mc-button.css';
import { injectInRenderContext } from '../util';

export type ButtonType = 'solid' | 'hollow' | 'outline';
export type ButtonVariant = 'filled' | 'outlined' | 'borderless';
export type ButtonSize = 'small' | 'medium' | 'large' | 'extraLarge';
export type IconPosition = 'left' | 'right' | 'dropdown';
export type ButtonState = 'default' | 'active' | 'checked';

export interface ButtonProps {
  // Content
  content?: JSXElement | string;
  children?: JSXElement | string;

  // Button appearance
  type?: ButtonType;
  variant?: ButtonVariant;
  color?: BtnClasses;
  size?: ButtonSize;
  shape?: 'ellipse' | 'rectangle' | 'square' | 'circle';

  fullWidth?: boolean;

  // Icon support
  icon?: string;
  iconColor?: IconColor;
  iconSize?: IconSize;
  iconPosition?: IconPosition;
  iconOnly?: boolean;
  iconClass?: string;
  imageSrc?: string;
  imageAlt?: string;
  /** When true (default), `imageSrc` renders as a circular avatar. Set false
   *  for square/SVG icons that shouldn't be clipped to a circle. */
  imageRounded?: boolean;

  // State
  disabled?: boolean;
  state?: ButtonState; // TODO: Not available in Modus - 'active' and 'checked' states

  // Behavior
  onClick?(): void; // Handler function passed from web component wrapper
  buttonType?: 'button' | 'submit' | 'reset';

  // Accessibility
  title?: string;
  ariaLabel?: string;
  'data-cy'?: string;

  // Additional classes
  class?: string;

  // Ref forwarding — receives the modus-wc-button element once mounted
  ref?(el: HTMLElement): void;
}

const Button: Component<ButtonProps> = (props): JSXElement => {
  let buttonRef: HTMLElement | undefined;

  onMount(() => {
    let cancelled = false;
    customElements.whenDefined('modus-wc-button').then(() => {
      if (cancelled || !buttonRef) return;
      injectInRenderContext(
        buttonRef,
        'mc-button-style',
        MODUS_BUTTON_INVERSE_STYLESHEET
      );
    });
    onCleanup(() => {
      cancelled = true;
    });
  });

  // Reactive getters for props
  const getContent = (): JSXElement | string =>
    props.content || props.children || '';
  const getType = (): ButtonType => props.type || 'solid';
  const getShape = (): string => props.shape || 'rectangle';
  const getColor = (): BtnClasses => props.color || BtnClasses.Primary;
  const getIconPosition = (): IconPosition => props.iconPosition || 'left';
  const getButtonType = (): 'button' | 'submit' | 'reset' =>
    props.buttonType || 'button';
  const getVariant = (): ButtonVariant => {
    if (props.variant) {
      return props.variant;
    }

    const type = getType();
    const color = getColor();

    if (type === 'hollow') return 'borderless';
    if (type === 'outline') return 'outlined';

    if (
      color === BtnClasses.LinkPrimary ||
      color === BtnClasses.LinkSecondary ||
      color === BtnClasses.LinkDanger
    ) {
      return 'borderless';
    }

    return 'filled';
  };

  const getModusSize = (): 'sm' | 'md' | 'lg' | 'xl' => {
    switch (props.size) {
      case 'small':
        return 'sm';
      case 'large':
        return 'lg';
      case 'extraLarge':
        return 'xl';
      case 'medium':
      default:
        return 'md';
    }
  };

  type ModusButtonColor =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'warning'
    | 'danger';

  const getModusColor = (): ModusButtonColor => {
    const color = getColor();

    switch (color) {
      case BtnClasses.LinkPrimary:
        return 'primary';
      case BtnClasses.LinkSecondary:
        return 'secondary';
      case BtnClasses.LinkDanger:
        return 'danger';
      case BtnClasses.Inverse:
      case BtnClasses.Default:
        return 'secondary';
      default:
        return color as ModusButtonColor;
    }
  };

  const getIconColor = (): IconColor | undefined => {
    if (props.iconColor !== undefined) {
      return props.iconColor;
    }

    switch (getColor()) {
      case BtnClasses.Primary:
        return IconColor.Inverse;
      case BtnClasses.LinkPrimary:
        return IconColor.Primary;
      case BtnClasses.Secondary:
      case BtnClasses.Default:
      case BtnClasses.LinkSecondary:
        return IconColor.TrimbleGray;
      case BtnClasses.Tertiary:
        return IconColor.Tertiary;
      default:
        return undefined;
    }
  };

  const getIconSize = (): IconSize => {
    if (props.iconSize) {
      return props.iconSize;
    }

    switch (props.size) {
      case 'small':
        return 'icon-small';
      case 'large':
        return 'icon-large';
      case 'medium':
      default:
        return 'icon-medium';
    }
  };

  const renderIcon = (): JSXElement => {
    if (props.imageSrc) {
      const imgClass =
        props.imageRounded === false
          ? 'tc-button-avatar-img tc-button-avatar-img--square'
          : 'tc-button-avatar-img';
      return (
        <img src={props.imageSrc} alt={props.imageAlt ?? ''} class={imgClass} />
      );
    }
    if (!props.icon) return null;

    return (
      <Icon
        icon={props.icon}
        color={getIconColor()}
        size={getIconSize()}
        customClass={props.iconClass}
      />
    );
  };

  // Dropdown arrow for dropdown buttons
  const renderDropdownArrow = (): JSXElement => {
    if (getIconPosition() !== 'dropdown') return null;
    return (
      <Icon
        icon="tc-icon-arrow-drop-down"
        color={getIconColor()}
        size="icon-small"
        aria-hidden="true"
      />
    );
  };

  // Check if has content
  const hasContent = (): boolean => {
    const content = getContent();
    return typeof content === 'string' ? content.trim().length > 0 : !!content;
  };

  const getCustomClass = (): string => {
    const classes: string[] = [];

    if (getColor() === BtnClasses.Inverse) {
      classes.push('inverse-button');
    }

    if (props.class) {
      classes.push(props.class);
    }

    return classes.join(' ');
  };

  return (
    <modus-wc-button
      ref={(el: HTMLElement) => {
        buttonRef = el;
        props.ref?.(el);
      }}
      color={getModusColor()}
      variant={getVariant()}
      size={getModusSize()}
      shape={getShape() as unknown as 'rectangle' | 'square' | 'circle'}
      disabled={props.disabled}
      full-width={props.fullWidth}
      type={getButtonType()}
      pressed={props.state === 'active' || props.state === 'checked'}
      aria-pressed={
        props.state === 'active' || props.state === 'checked'
          ? 'true'
          : undefined
      }
      custom-class={getCustomClass()}
      aria-label={props.ariaLabel}
      /*
       * on:click (not onClick) forces Solid to call addEventListener directly on this
       * element instead of using its default delegated-event system (a single listener
       * at the app root that resolves handlers via event.target). When this button is
       * portaled into SR1 (tc-modus-navbar's shadow root), a click that bubbles out of
       * SR1 gets its event.target retargeted to the shadow host, so the delegated
       * listener can never resolve back to this element. A direct listener fires during
       * the normal bubble phase inside SR1, before any retargeting occurs.
       */
      on:click={() => {
        if (!props.disabled) {
          props.onClick?.();
        }
      }}
      attr:data-testid={props['data-cy'] || 'button'}
    >
      {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
      <Show when={props.iconOnly}>{renderIcon()}</Show>
      <Show when={!props.iconOnly}>
        {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
        {getIconPosition() === 'left' && renderIcon()}
        <Show when={hasContent()}>
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          <span>{getContent()}</span>
        </Show>
        {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
        {getIconPosition() === 'right' && renderIcon()}
        {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
        {renderDropdownArrow()}
      </Show>
    </modus-wc-button>
  );
};

export default Button;
